import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getHeaderNavItems } from "../utils/authPermissions";

export default function AppHeader({ showBreadcrumbs = true, sx = {
    bgcolor: "#003376",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
} }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const [navAnchor, setNavAnchor] = useState(null);
    const [userAnchor, setUserAnchor] = useState(null);

    const navItems = getHeaderNavItems(user?.role || "");

    const currentItem =
        navItems.find((item) => item.path === location.pathname) || null;

    const displayName = user?.full_name || user?.username || "";
    const initials = displayName
        ?.split(" ")
        ?.map((w) => w[0])
        ?.join("")
        ?.slice(0, 2)
        ?.toUpperCase();

    const handleLogout = async () => {
        setUserAnchor(null);
        await logout();
        navigate("/");
    };

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={sx}
        >
            <Toolbar
                variant="dense"
                sx={{
                    minHeight: 60,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/assets/Mapgeoid-logo.jpeg`}
                        alt="HCG Logo"
                        sx={{
                            width: 150,
                            objectFit: "contain",
                            borderRadius: 1,
                            backgroundColor: "#fff",
                            p: 0.5,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                    />

                    <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(255,255,255,0.85)",
                                fontWeight: 600,
                            }}
                        >
                            Haryana City Gas Distribution
                        </Typography>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                {user && (
                    <Avatar
                        onClick={(e) => setUserAnchor(e.currentTarget)}
                        sx={{
                            width: 36,
                            height: 36,
                            fontSize: 13,
                            fontWeight: 700,
                            bgcolor: "#1976D2",
                            border: "2px solid rgba(255,255,255,0.15)",
                            cursor: "pointer",
                            "&:hover": {
                                bgcolor: "#1565C0",
                            },
                        }}
                    >
                        {initials}
                    </Avatar>
                )}
            </Toolbar>

            {showBreadcrumbs && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: "#F8FAFC",
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <Breadcrumbs
                        separator={
                            <NavigateNextIcon
                                fontSize="small"
                                sx={{ color: "#64748B" }}
                            />
                        }
                    >
                        <Box
                            onClick={() => navigate("/")}
                            sx={{
                                cursor: "pointer",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "20px",
                                bgcolor: "#E3F2FD",
                                color: "#1565C0",
                                fontSize: 12,
                                fontWeight: 600,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    bgcolor: "#BBDEFB",
                                },
                            }}
                        >
                            Home
                        </Box>

                        {currentItem && (
                            <Box
                                onClick={(e) => setNavAnchor(e.currentTarget)}
                                sx={{
                                    cursor: "pointer",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: "20px",
                                    bgcolor: "#1976D2",
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "#1565C0",
                                    },
                                }}
                            >
                                {currentItem.label}
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                            </Box>
                        )}
                    </Breadcrumbs>

                    <Menu
                        anchorEl={navAnchor}
                        open={Boolean(navAnchor)}
                        onClose={() => setNavAnchor(null)}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                borderRadius: 2,
                                minWidth: 200,
                                boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
                            },
                        }}
                    >
                        {navItems.map((item) => (
                            <MenuItem
                                key={item.path}
                                selected={item.path === location.pathname}
                                sx={{
                                    fontSize: 14,
                                    "&.Mui-selected": {
                                        backgroundColor: "#E3F2FD",
                                        color: "#1565C0",
                                        fontWeight: 600,
                                    },
                                }}
                                onClick={() => {
                                    navigate(item.path);
                                    setNavAnchor(null);
                                }}
                            >
                                {item.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            )}

            <Menu
                anchorEl={userAnchor}
                open={Boolean(userAnchor)}
                onClose={() => setUserAnchor(null)}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        minWidth: 240,
                        boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <MenuItem disabled>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {displayName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666" }}>
                            {user?.email}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: "#999", display: "block" }}
                        >
                            Role: {user?.role}
                        </Typography>
                    </Box>
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
        </AppBar>
    );
}
